import type { Key, ReactNode } from "react";

import { AnimatePresence, motion, MotionConfig } from "framer-motion";

const PANEL_WIDTH = 376;
const PANEL_TRANSITION = { duration: 0.35, ease: [0.32, 0.72, 0, 1] } as const;
const SWAP_TRANSITION = { duration: 0.2, ease: "easeOut" } as const;

export type SwapDirection = -1 | 0 | 1;

export function AnimatedSidePanel({
  children,
  contentKey,
  direction = 0,
  isOpen,
}: {
  children: ReactNode;
  contentKey: Key;
  direction?: SwapDirection;
  isOpen: boolean;
}) {
  return (
    <MotionConfig reducedMotion="user">
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            animate={{ opacity: 1, width: PANEL_WIDTH }}
            className="shrink-0 overflow-hidden"
            exit={{ opacity: 0, width: 0 }}
            initial={{ opacity: 0, width: 0 }}
            key="side-panel"
            transition={PANEL_TRANSITION}
          >
            <motion.div
              animate={{ x: 0 }}
              className="pl-lg"
              exit={{ x: 32 }}
              initial={{ x: 32 }}
              style={{ width: PANEL_WIDTH }}
              transition={PANEL_TRANSITION}
            >
              <AnimatePresence custom={direction} initial={false} mode="wait">
                <motion.div
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  custom={direction}
                  exit="leave"
                  initial="enter"
                  key={contentKey}
                  transition={SWAP_TRANSITION}
                  variants={{
                    enter: (step: SwapDirection) => ({
                      opacity: 0,
                      x: step * 24,
                      y: step === 0 ? 8 : 0,
                    }),
                    leave: (step: SwapDirection) => ({
                      opacity: 0,
                      x: step * -24,
                      y: step === 0 ? -8 : 0,
                    }),
                  }}
                >
                  {children}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </MotionConfig>
  );
}
